// scoringConfig.ts - Default scoring configuration template with types
//
// Field properties:
// - disabled: UI greyed out, can't interact
// - required: Must have value before save
// - locked: Needs addon purchase to unlock (paywall)
// - active: Rule applies during scoring
// - hidden: Not shown in UI at all
// - frozen: Cannot edit after contest starts/publishes
// - available: Whether this field is available for the series (e.g. set false to hide for specific series)

// Types
export type ScoringFieldType = 'number' | 'boolean' | 'strikeRate' | 'economyRate';
export type ScoringOperation = 'addition' | 'substract' | 'multiplier' | null;

export interface StrikeRateRange {
  type: 'above' | 'below';
  threshold: number;
  points: number;
}

export interface StrikeRateValue {
  minBalls: number;
  ranges: StrikeRateRange[];
}

export interface EconomyRateRange {
  type: 'above' | 'below';
  threshold: number;
  points: number;
}

export interface EconomyRateValue {
  minOvers: number;
  ranges: EconomyRateRange[];
}

export type ScoringFieldValue = number | boolean | StrikeRateValue | EconomyRateValue | null;

export interface ScoringField {
  key: string;
  label: string;
  type: ScoringFieldType;
  default: ScoringFieldValue;
  value: ScoringFieldValue;
  operation: ScoringOperation;
  disabled: boolean;
  required: boolean;
  locked: boolean;
  active: boolean;
  hidden: boolean;
  frozen: boolean;
  available: boolean;
  mutuallyExclusive?: string;
}

export interface ScoringSection {
  section: string;
  fields: ScoringField[];
  notes?: string[];
}

export interface ScoringConfig {
  scoringConfig: ScoringSection[];
}

// Helper functions for manipulating scoring config

/**
 * Get a field by key from any section
 */
export const getFieldByKey = (config: ScoringSection[], key: string): ScoringField | undefined => {
  for (const section of config) {
    const field = section.fields.find(f => f.key === key);
    if (field) return field;
  }
  return undefined;
};

/**
 * Get a section by name
 */
export const getSectionByName = (config: ScoringSection[], name: string): ScoringSection | undefined => {
  return config.find(s => s.section.toLowerCase() === name.toLowerCase());
};

/**
 * Update a field value by key
 */
export const updateFieldValue = (
  config: ScoringSection[],
  key: string,
  value: ScoringFieldValue
): ScoringSection[] => {
  return config.map(section => ({
    ...section,
    fields: section.fields.map(field =>
      field.key === key ? { ...field, value } : field
    )
  }));
};

/**
 * Toggle a field's active state, handling mutually exclusive fields
 */
export const toggleFieldActive = (
  config: ScoringSection[],
  key: string,
  active: boolean
): ScoringSection[] => {
  const targetField = getFieldByKey(config, key);
  if (!targetField) return config;

  return config.map(section => ({
    ...section,
    fields: section.fields.map(field => {
      if (field.key === key) {
        return { ...field, active };
      }
      // Deactivate mutually exclusive field when activating
      if (active && targetField.mutuallyExclusive && field.key === targetField.mutuallyExclusive) {
        return { ...field, active: false };
      }
      return field;
    })
  }));
};

/**
 * Deep clone a scoring config
 */
export const cloneScoringConfig = (config: ScoringSection[]): ScoringSection[] => {
  return JSON.parse(JSON.stringify(config));
};

/**
 * Reset all field values to their defaults
 */
export const resetToDefaults = (config: ScoringSection[]): ScoringSection[] => {
  return config.map(section => ({
    ...section,
    fields: section.fields.map(field => ({
      ...field,
      value: field.default,
    }))
  }));
};

/**
 * Get only active, non-hidden, and available fields
 */
export const getVisibleFields = (section: ScoringSection): ScoringField[] => {
  return section.fields.filter(f => !f.hidden && f.available);
};

/**
 * Get only active fields for scoring calculation
 */
export const getActiveFields = (section: ScoringSection): ScoringField[] => {
  return section.fields.filter(f => f.active && f.available);
};

/**
 * Check if any field is frozen (for published contests)
 */
export const hasEditableFields = (config: ScoringSection[], isPublished: boolean): boolean => {
  for (const section of config) {
    for (const field of section.fields) {
      if (!field.hidden && field.active && !field.disabled && field.available) {
        if (!isPublished || !field.frozen) {
          return true;
        }
      }
    }
  }
  return false;
};

/**
 * Validate a scoring config
 */
export const validateScoringConfig = (config: ScoringSection[]): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  for (const section of config) {
    if (!section.section) {
      errors.push('Section name is required');
    }

    const fieldKeys = new Set<string>();
    for (const field of section.fields) {
      if (!field.key) {
        errors.push(`Field in section "${section.section}" is missing a key`);
      }
      if (!field.label) {
        errors.push(`Field "${field.key}" in section "${section.section}" is missing a label`);
      }
      if (fieldKeys.has(field.key)) {
        errors.push(`Duplicate field key "${field.key}" in section "${section.section}"`);
      }
      fieldKeys.add(field.key);

      if (field.required && (field.value === null || field.value === undefined)) {
        errors.push(`Required field "${field.label}" in section "${section.section}" has no value`);
      }

      // Check mutually exclusive conflicts
      if (field.mutuallyExclusive && field.active) {
        const exclusiveField = getFieldByKey(config, field.mutuallyExclusive);
        if (exclusiveField?.active) {
          errors.push(`"${field.label}" and "${exclusiveField.label}" are mutually exclusive — only one can be active`);
        }
      }
    }
  }

  return { valid: errors.length === 0, errors };
};

// Default scoring configuration
export const defaultScoringConfig: ScoringSection[] = [
  {
    section: "Batting",
    fields: [
      {
        key: "per_run",
        label: "Per Run",
        type: "number",
        default: 1,
        value: 1,
        operation: "addition",
        disabled: false,
        required: true,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "four_bonus",
        label: "Every Four Bonus",
        type: "number",
        default: 2,
        value: 2,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "six_bonus",
        label: "Every Six Bonus",
        type: "number",
        default: 3,
        value: 3,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "thirty_run_bonus",
        label: "30 Run Bonus",
        type: "number",
        default: 5,
        value: 5,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
        mutuallyExclusive: "every_thirty_run_bonus",
      },
      {
        key: "every_thirty_run_bonus",
        label: "Every 30 Run Bonus",
        type: "number",
        default: 5,
        value: 5,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: false,
        hidden: false,
        frozen: false,
        available: true,
        mutuallyExclusive: "thirty_run_bonus",
      },
      {
        key: "half_century_bonus",
        label: "Half Century Bonus",
        type: "number",
        default: 10,
        value: 10,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "century_bonus",
        label: "Century Bonus",
        type: "number",
        default: 20,
        value: 20,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "duck_penalty",
        label: "Dismissal for Duck",
        type: "number",
        default: 5,
        value: 5,
        operation: "substract",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "exclude_bowler_duck",
        label: "Exclude Bowler for Duck",
        type: "boolean",
        default: false,
        value: false,
        operation: null,
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "strike_rate",
        label: "Strike Rate Bonus",
        type: "strikeRate",
        default: {
          minBalls: 10,
          ranges: [
            { type: "above", threshold: 100, points: 4 },
            { type: "below", threshold: 70, points: -2 },
          ],
        },
        value: {
          minBalls: 10,
          ranges: [
            { type: "above", threshold: 100, points: 4 },
            { type: "below", threshold: 70, points: -2 },
          ],
        },
        operation: null,
        disabled: false,
        required: false,
        locked: false,
        active: false,
        hidden: false,
        frozen: false,
        available: true,
      },
    ],
    notes: [
      "Only points provided to Century; Half Century and 30 Run Bonus will not be applied.",
      "30 Run Bonus and Every 30 Run Bonus are mutually exclusive — only one can be active.",
    ],
  },
  {
    section: "Bowling",
    fields: [
      {
        key: "per_wicket",
        label: "Per Wicket",
        type: "number",
        default: 25,
        value: 25,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "maiden_over",
        label: "Per Maiden Over",
        type: "number",
        default: 10,
        value: 10,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "bowled_bonus",
        label: "Bowled Bonus",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "lbw_bonus",
        label: "LBW Bonus",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "three_wicket_bonus",
        label: "3 Wicket Haul Bonus",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
        mutuallyExclusive: "every_three_wicket_bonus",
      },
      {
        key: "every_two_wicket_bonus",
        label: "Every 2 Wicket Bonus",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: false,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "every_three_wicket_bonus",
        label: "Every 3 Wicket Bonus",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: false,
        hidden: false,
        frozen: false,
        available: true,
        mutuallyExclusive: "three_wicket_bonus",
      },
      {
        key: "four_wicket_bonus",
        label: "4 Wicket Haul Bonus",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "five_wicket_bonus",
        label: "5 Wicket Haul Bonus",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "economy_rate",
        label: "Economy Rate Bonus",
        type: "economyRate",
        default: {
          minOvers: 2,
          ranges: [
            { type: "below", threshold: 6, points: 4 },
            { type: "above", threshold: 10, points: -2 },
          ],
        },
        value: {
          minOvers: 2,
          ranges: [
            { type: "below", threshold: 6, points: 4 },
            { type: "above", threshold: 10, points: -2 },
          ],
        },
        operation: null,
        disabled: false,
        required: false,
        locked: false,
        active: false,
        hidden: false,
        frozen: false,
        available: true,
      },
    ],
    notes: [
      "Bonus points for 4 and 5 wicket hauls are awarded separately.",
      "3 Wicket Haul Bonus and Every 3 Wicket Bonus are mutually exclusive — only one can be active.",
    ],
  },
  {
    section: "Fielding",
    fields: [
      {
        key: "per_catch",
        label: "Per Catch",
        type: "number",
        default: 10,
        value: 10,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "per_runout",
        label: "Per Runout",
        type: "number",
        default: 10,
        value: 10,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "per_stumping",
        label: "Per Stumping",
        type: "number",
        default: 10,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
    ],
    notes: [
      "Multiple players involved in a run-out share the credit equally.",
    ],
  },
  {
    section: "Other Bonus",
    fields: [
      {
        key: "captain_bonus",
        label: "Captain",
        type: "number",
        default: 2,
        value: 2,
        operation: "multiplier",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "vice_captain_bonus",
        label: "Vice Captain",
        type: "number",
        default: 1.5,
        value: 1.5,
        operation: "multiplier",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "orange_cap",
        label: "Orange Cap",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "purple_cap",
        label: "Purple Cap",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "man_of_match",
        label: "Man of the Match",
        type: "number",
        default: 50,
        value: 50,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "man_of_series",
        label: "Man of the Series",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
      {
        key: "announced_in_lineup",
        label: "Announced in Lineups",
        type: "number",
        default: 0,
        value: 0,
        operation: "addition",
        disabled: false,
        required: false,
        locked: false,
        active: true,
        hidden: false,
        frozen: false,
        available: true,
      },
    ],
    notes: [
      "Captain gets 2x points, Vice Captain gets 1.5x points.",
    ],
  },
];

export default defaultScoringConfig;
